import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, ShieldCheck, BadgeCheck } from 'lucide-react';
import homeadvisor2015 from '../assets/homeadvisor-2015.webp';
import homeadvisor2016 from '../assets/homeadvisor-2016.webp';
import homeadvisor2017 from '../assets/homeadvisor-2017.webp';
import homeadvisor2018 from '../assets/homeadvisor-2018.webp';
import homeadvisor2019 from '../assets/homeadvisor-2019.webp';
import homeadvisor2021 from '../assets/homeadvisor-2021.webp';

const BADGES = [
  { year: '2015', image: homeadvisor2015 },
  { year: '2016', image: homeadvisor2016 },
  { year: '2017', image: homeadvisor2017 },
  { year: '2018', image: homeadvisor2018 },
  { year: '2019', image: homeadvisor2019 },
  { year: '2021', image: homeadvisor2021 },
];

const CREDENTIALS = [
  {
    icon: ShieldCheck,
    title: 'Fully Insured & Bonded',
    description: 'Complete peace of mind with full insurance coverage for every job.',
  },
  {
    icon: BadgeCheck,
    title: 'Background Checked',
    description: 'All team members undergo thorough background screening.',
  },
  {
    icon: Award,
    title: '15+ Years Experience',
    description: 'Trusted by hundreds of families across Montgomery County since 2008.',
  },
];

function AwardBadge({ year, image }: { year: string; image: string }) {
  return (
    <div className="w-full aspect-square rounded-2xl bg-white/[0.04] border border-white/[0.08] flex flex-col items-center justify-center gap-2 p-3 hover:bg-white/[0.08] hover:border-white/[0.12] transition-all duration-300">
      <img src={image} alt={`Best of HomeAdvisor ${year} Winner`} className="w-full h-full object-contain" />
      <span className="text-xs font-semibold text-axis-200 tracking-wide">{year}</span>
    </div>
  );
}

export default function Awards() {
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: '-100px' });

  return (
    <section className="relative py-24 sm:py-32 bg-axis-950 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>
      <div className="absolute top-0 left-0 w-[450px] h-[450px] bg-axis-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-sand-500/5 rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto section-padding relative z-10">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6 border border-white/10">
            <Award className="w-4 h-4 text-axis-300" />
            <span className="text-sm font-medium text-axis-300">Awards &amp; Certifications</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
            Award-Winning
            <br />
            <span className="text-axis-400 italic">Quality Service</span>
          </h2>
          <p className="text-axis-300/70 text-lg">
            Recognized year after year by HomeAdvisor for outstanding service and customer satisfaction.
          </p>
        </motion.div>

        {/* Badges */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 sm:gap-6 mb-16 max-w-4xl mx-auto">
          {BADGES.map((badge, i) => (
            <motion.div
              key={badge.year}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <AwardBadge year={badge.year} image={badge.image} />
            </motion.div>
          ))}
        </div>

        {/* Credentials */}
        <div className="grid sm:grid-cols-3 gap-6">
          {CREDENTIALS.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 lg:p-8 hover:bg-white/[0.08] hover:border-white/[0.12] transition-all duration-500"
            >
              <div className="w-12 h-12 rounded-xl bg-axis-500/15 text-axis-300 border border-axis-400/20 flex items-center justify-center mb-5">
                <item.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-axis-300/60 leading-relaxed text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
